<template>
  <div class="videos">
    <Hero
      class="hero"
      heading="Videos"
    />
    <div class="action-bar-container">
      <EditToggle v-if="hasEditTogglePermissions" :callback="editToggle" class="edit-toggle" />
    </div>
    <div class="page-intro">
      <h2 class="page-intro-header">Videos</h2>
      <div class="text-block">
        These videos are meant to be seen! Just keep
          in mind that we rather not send big files to our
          customers when we don't have to, since we talk a
          lot about the environmental impact of data transfer.<br/>
          Show it, don't send it!
      </div>
    </div>
    <div
      v-for="(topicData, topicKey) in mappedFiles"
      :key="topicKey"
      class="topic-container"
    >

      <div :id="'anchor-' + topicKey" class="header-container">
        <div class="header-topic-container">
          <h2>{{ topicData.displayName }}</h2>
          <div
            v-if="hasCreatePermissions"
            @click="showModal[topicKey].create = true"
            class="icon new-icon">
          </div>
        </div>
        <TextBlock v-if="topicData.description" v-html="topicData.description"></TextBlock>
      </div>

      <Modal
        title="Upload File"
        :show="showModal[topicKey].create"
        @close="showModal[topicKey].create = false">
        <FileUploaderMultiple
          :files="[
            {
              accepts: '.zip',
              required: true,
              onSelectFilesMapper: onSelectVideoMapper,
            },
            {
              accepts: 'image/*',
              required: true,
              canUpdateDisplayName: false,
              onSelectFilesMapper: onSelectImageMapper
            }
          ]"
          :topicKey="topicKey"
          :securityLevel="'private'"
          :isReplace=false
          :unionHash="generateHash(12)"
          :successCallback="filesUpdate"
        >
        </FileUploaderMultiple>
      </Modal>

      <div class="surround-all-containers">
        <div
          v-for="file in topicData.files"
          :key="file.key"
          class="video-container"
        >
          <img :src="file.unionFile && cacheBustingImageUrl(file.unionFile.dynamicURL)"/>
          <div class="action-bar">
            <div>{{ file.alias }}</div>
            <img @click="download(file.dynamicURL)" src="/icons/download-arrow.svg" />
            <div
              v-if="hasReplacePermissions"
              @click="showModal[topicKey].replace[file.key] = true"
              class="replace-icon">
            </div>
            <div
              v-if="hasDeletePermissions"
              @click="showModal[topicKey].remove[file.key] = true"
              class="delete-icon">
            </div>
          </div>

          <Modal
            title="Replace File"
            :show="showModal[topicKey].replace[file.key]"
            @close="showModal[topicKey].replace[file.key] = false">
            <FileUploaderMultiple
              :files="[
                {
                  ...file,
                  accepts: '.zip',
                  required: false,
                  onSelectFilesMapper: onSelectVideoMapper,
                },{
                  ...file.unionFile,
                  accepts: 'image/*',
                  required: false,
                  canUpdateDisplayName: false,
                  onSelectFilesMapper: onSelectImageMapper
                }
              ]"
              :topicKey="topicKey"
              :securityLevel="'private'"
              :isReplace=true
              :unionHash="file.metadata.unionHash"
              :successCallback="filesUpdate"
            >
            </FileUploaderMultiple>
          </Modal>
          <Modal
            title="Delete File"
            :show="showModal[topicKey].remove[file.key]"
            @close="showModal[topicKey].remove[file.key] = false">
            <div class="delete-dialog">
              <p>Are you sure you want to delete the video:</p>
              <span class="display-name">{{ file.alias }}</span>
              <button @click="deleteFiles(file, topicKey)" class="btn">Delete</button>
            </div>
          </Modal>
        </div>
      </div>
    </div>
    <ViLinks />
  </div>
</template>
<script>
import Hero from '@/components/hero.vue';
import TextBlock from '@/components/text-block.vue';
import ViLinks from '@/components/visual-id-links.vue';
import EditToggle from '@/components/edit-toggle.vue';
import Modal from '@/components/modal.vue';
import FileUploaderMultiple from '@/components/file-uploader-multiple.vue';

import DeleteObjects from '@/handlers/deleteObjects';

import generateRandomHash from '@/utils/generateRandomHash';
import resizeImage from '@/utils/resizeImage';
import imageGetRatio from '@/utils/imageGetRatio';
import zipDecompress from '@/utils/zipDecompress';

import MixinPermissions from '@/mixins/mixin-permissions';
import MixinFileListing from '@/mixins/mixin-file-listing';

export default {
  name: 'Videos',
  components: {
    Hero,
    TextBlock,
    ViLinks,
    EditToggle,
    Modal,
    FileUploaderMultiple,
  },
  mixins: [
    MixinPermissions,
    MixinFileListing(
      {
        key_brand_videos: {
          displayName: 'Brand',
          files: [],
          description: '',
        },
        key_product_videos: {
          displayName: 'Product',
          files: [],
          description: '',
        },
        key_tutorial_videos: {
          displayName: 'Tutorial',
          files: [],
          description: '',
        },
        key_unbranded_videos: {
          displayName: 'Unbranded creatives',
          files: [],
          description: '',
        },
        key_unique_videos: {
          displayName: 'Unique selling point',
          files: [],
          description: '',
        },
      },
      'private',
      '',
    ),
  ],
  data() {
    return {
      securityLevel: 'private',
      editMode: false,
      showModal: {},
      mappedFiles: {},
    };
  },
  watch: {
    files: {
      deep: true,
      handler() {
        this.mapFiles();
      },
    },
    mappedFiles: {
      deep: true,
      handler() {
        this.initializeShowModal();
      },
    },
  },
  methods: {
    download(url) {
      window.open(url);
    },
    editToggle(onOrOff) {
      this.editMode = onOrOff;
    },
    fileMapper(files) {
      const mappedFiles = {};
      Object.keys(files).forEach((topicKey) => {
        mappedFiles[topicKey] = {
          ...files[topicKey],
          // here we overwrite the files array from the original object with
          // one where any union file(ie. image) have been mapped to their video file
          files: files[topicKey].files
            // filter out that we only keep the videos
            .filter(file => file.metadata && file.metadata.type && ['application/zip', 'video'].includes(file.metadata.type))
            // for each video we loop over the original files list to find the image union file
            // (i.e the image that has the same unionHash as the video)
            .map(video => ({
              ...video,
              unionFile: files[topicKey].files
                .find(icon => icon.metadata && icon.metadata.type && icon.metadata.type.startsWith('image/')
                  && icon.metadata.unionHash === video.metadata.unionHash),
            })),
        };
      });

      return mappedFiles;
    },
    mapFiles() {
      this.$set(this, 'mappedFiles', this.fileMapper(this.files));
    },
    initializeShowModal() {
      Object.keys(this.mappedFiles).forEach((topicKey) => {
        const replace = {};
        const remove = {};
        this.mappedFiles[topicKey].files.forEach((file) => {
          replace[file.key] = false;
          remove[file.key] = false;
        });
        this.$set(this.showModal, topicKey, {
          replace,
          remove,
          create: false,
        });
      });
    },
    generateHash(length) {
      return generateRandomHash(length);
    },
    cacheBustingImageUrl(url) {
      return `${url}?v=${Date.now()}`;
    },
    filesUpdate() {
      // function can be found found in mixin-file-listing.js
      this.generateFilelist();
    },
    deleteFiles(file, topicKey) {
      const deleteFileKeys = [];
      deleteFileKeys.push(file.key);
      this.files[topicKey].files.forEach((unionFile) => {
        if (unionFile.metadata && unionFile.metadata.unionHash === file.metadata.unionHash) {
          deleteFileKeys.push(unionFile.key);
        }
      });

      DeleteObjects(deleteFileKeys, this.filesUpdate);
    },
    async onSelectImageMapper(files) {
      // Check that image complies with dimentions ratio specifications
      const ratios = await Promise.all(files.map(_file => imageGetRatio(_file)));
      const isCorrectRatio = ratios.every((ratio) => {
        const targetRatio = 16 / 9;
        return Math.abs(ratio - targetRatio) <= targetRatio * 0.05;
      });

      if (!isCorrectRatio) {
        throw new Error('Image file should be in ration approximate to 16:9 (eg. 270 x 150)');
      }

      // Resize image to height 150 will retaining dimentions ratio
      const resizedImagesPromises = files.map(_file => resizeImage(_file, 150));
      const resizedImages = (await Promise.allSettled(resizedImagesPromises))
        .map(_file => _file.value);

      return resizedImages;
    },
    async onSelectVideoMapper(files) {
      const allowedExtensions = ['mp4', 'mkv', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mpeg'];
      const promises = files.map(zip => zipDecompress(zip, allowedExtensions));
      await Promise.all(promises);

      return files;
    },
  },
};
</script>


  <style lang="stylus" scoped>
  @require '../style/colors'
  .page-intro
    display: flex
    flex-wrap: wrap
    justify-content: center
    max-width: 65rem
    margin: 5rem auto 10rem auto
    padding: 0 2.5rem

    .page-intro-header
      font-weight: normal
      text-align: center
      font-size: 36px

  .surround-all-containers
    display: flex
    flex-wrap: wrap
    justify-content: center
    gap: 1.5rem
    max-width: 95rem
    margin: 0 auto 5rem auto
    padding: 0 2rem

    .video-container
      width: 26.3rem

      img
        height: 15rem

      .action-bar
        display: flex
        flex-wrap: wrap
        justify-content: flex-start
        gap: 0.8rem
        margin-top: 0.5rem
        font-size: 1.6rem
        font-weight: 300
        color: $primary-color
        cursor: pointer

        img
          height: 1.5rem
          padding: .3rem

  .header-container
    display: flex
    flex-direction: column
    align-items: center
    width: 100%

    .header-topic-container
      display: grid
      grid-template-columns: 1fr 1fr 1fr
      justify-items: center
      align-items: center
      gap: 5rem

      h2
        grid-column: 2
        text-align: center

      .icon
        position: relative
        width: 2.5rem
        height: 2.5rem
        cursor: pointer
      .new-icon
        background: url('../assets/addButton.svg') no-repeat
        background-size: contain
        grid-column: 3
        justify-self: start

      .icon:hover
        cursor: pointer

  .hero
    margin-bottom: 30px

  .download-links
    display: flex
    flex-direction: column
    padding: 0 60px
    max-width: 100rem
    justify-content: center
    margin: 0 auto 90px auto
  .btn
    margin-top: 20px

  .topic-container
    margin: 60px 0 0 0

  .delete-icon
  .replace-icon
  .edit-icon
    width: 2rem
    height: 2rem
    background-size: contain
    cursor: pointer
  .delete-icon
    background: url('../assets/delete_document.svg') no-repeat
  .replace-icon
    background: url('../assets/update_content_document.svg') no-repeat
  .edit-icon
    background: url('../assets/edit_name_document.svg') no-repeat
    width: 25px
    height: 25px

  .action-bar-container
    display: grid
    grid-template-columns: 1fr 1fr 1fr
    justify-items: center
    align-items: center
    margin-bottom: 2rem
    gap: 4rem

    .edit-toggle
      grid-column: 3
      justify-self: end
      align-self: flex-start

  .delete-dialog
    display: flex
    flex-direction: column
    align-items: center

    p
      font-size: 2.2rem
      margin-bottom: 2rem

    button
      background-color: $primary-color
      color: $white
      font-size: 2.2rem
      padding: 1.5rem 3rem
      border: none
      border-radius: .5rem
      cursor: pointer
      transition: background-color 0.3s ease-in-out

      &:hover
        background-color: darken($primary-color, 10%)

  </style>
